'use client';
import './Pagination.css';
import { ReactNode } from 'react';
import { Link } from '../Link/Link';

interface PaginationProps {
	count: number;
	limit: number;
	page: number;
	setPage: React.Dispatch<React.SetStateAction<number>> | null; // if null, the pagination is based on the query string.
	t?: any;
}

const ChevronLeft = () => (
	<svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth={1.5}>
		<polyline points='15 18 9 12 15 6' />
	</svg>
);

const ChevronRight = () => (
	<svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth={1.5}>
		<polyline points='9 18 15 12 9 6' />
	</svg>
);

const Pagination = ({ count, limit, page, setPage, t }: PaginationProps) => {
	let pages: ReactNode[] = [];
	const pagesCount = Math.ceil(count! / limit);

	if (pagesCount <= 1) {
		return null;
	}

	for (let i = 1; i <= pagesCount; i++) {
		if (!((i >= page - 1 && i <= page + 1) || i === 1 || i === pagesCount)) {
			continue;
		}

		if (i === pagesCount && page + 1 < pagesCount - 1) {
			pages.push(
				<button className='pagination__page pagination__page--ellipsis' key='ellipsis-right'>
					...
				</button>,
			);
		}

		if (i === page) {
			pages.push(
				<button className='pagination__page pagination__page--selected' key={i}>
					{i}
				</button>,
			);
		} else {
			if (setPage == null) {
				pages.push(
					<Link className='pagination__page' path={`/products?page=${i}`} key={i}>
						{i}
					</Link>,
				);
			} else {
				pages.push(
					<button className='pagination__page' onClick={() => setPage(i)} key={i}>
						{i}
					</button>,
				);
			}
		}

		if (i === 1 && page - 1 > 2) {
			pages.push(
				<button className='pagination__page pagination__page--ellipsis' key='ellipsis-left'>
					...
				</button>,
			);
		}
	}

	const ofLabel = t == null ? 'di' : t('Of');
	const previousLabel = t == null ? 'Precedente' : t('Previous');
	const nextLabel = t == null ? 'Successivo' : t('Next');

	let countSummary: ReactNode = null;
	const start = (page - 1) * limit + 1;
	let end = start + limit - 1;
	if (end > count!) {
		end = count!;
	}
	countSummary = `${start}-${end} ${ofLabel} ${count}`;

	return (
		<div className='pagination'>
			<div className='pagination__count'>{countSummary}</div>
			{pages.length > 1 && (
				<div className='pagination__pages'>
					{page === 1 ? (
						<span className='pagination__page pagination__page--previous pagination__page--disabled'>
							<ChevronLeft />
							{previousLabel}
						</span>
					) : setPage == null ? (
						<Link className='pagination__page pagination__page--previous' path={`/products?page=${page - 1}`}>
							<ChevronLeft />
							{previousLabel}
						</Link>
					) : (
						<button className='pagination__page pagination__page--previous' onClick={() => setPage(page - 1)}>
							<ChevronLeft />
							{previousLabel}
						</button>
					)}
					<div className='pagination__numbers'>{pages}</div>
					{page === pagesCount ? (
						<span className='pagination__page pagination__page--next pagination__page--disabled'>
							{nextLabel}
							<ChevronRight />
						</span>
					) : setPage == null ? (
						<Link className='pagination__page pagination__page--next' path={`/products?page=${page + 1}`}>
							{nextLabel}
							<ChevronRight />
						</Link>
					) : (
						<button className='pagination__page pagination__page--next' onClick={() => setPage(page + 1)}>
							{nextLabel}
							<ChevronRight />
						</button>
					)}
				</div>
			)}
		</div>
	);
};

export { Pagination };
